import base64 from 'react-native-base64';
import { firebase } from '../config/configureFirebase';
import AccessKey from './AccessKey';
import store from '../store/store';

// In-memory caches for Google API functions.
//
// TTLs aggressively bumped (vs the earlier 2/30-min values) — autocomplete
// results for "bole" don't change meaningfully between morning and night,
// and a Google place_id's coordinates are effectively permanent. Combined
// with the OSM landmarks `searchLandmarks()` local-first decision tree
// (plans/linear-waddling-peacock.md), this catches most of the residual
// queries that DO fall through to Google so they're free on the second
// hit. localStorage persistence below extends that benefit across page
// reloads on web; mobile resets per-session (acceptable trade-off given
// the bundled landmarks cover most queries anyway).
const autocompleteCache = new Map();
const AUTOCOMPLETE_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const AUTOCOMPLETE_CACHE_MAX = 500;

const placeCoordCache = new Map();
const PLACE_COORD_CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days — coords are immutable
const PLACE_COORD_CACHE_MAX = 1000;

const directionsCache = new Map();
const DIRECTIONS_CACHE_TTL = 300000; // 5 minutes
const DIRECTIONS_CACHE_MAX = 30;

const distanceMatrixCache = new Map();
const DISTANCE_MATRIX_CACHE_TTL = 300000; // 5 minutes
const DISTANCE_MATRIX_CACHE_MAX = 30;

// ── Persistent-cache layer (web-only) ────────────────────────────────
// Hydrates the in-memory autocomplete + place-coord caches from
// localStorage on module load, and persists each new entry back. This
// makes the cache survive page reloads — common in the web admin where
// staff reload their dashboard several times a day. React Native has
// no window.localStorage, so all calls are no-ops there; the in-memory
// cache is the only layer that exists for mobile.
//
// Storage budget: each autocomplete entry is ~2 KB (10 results × 200B
// each). 500 entries × 2 KB = ~1 MB. Each place-coord entry is ~50 B
// × 1000 = ~50 KB. Well under the 5-10 MB localStorage quota.
const LS_AUTOCOMPLETE_KEY = '__ddPlacesAutocompleteCache_v1';
const LS_PLACECOORD_KEY = '__ddPlaceCoordCache_v1';

const _hasLocalStorage = (() => {
    try {
        return typeof window !== 'undefined'
            && typeof window.localStorage !== 'undefined';
    } catch (_) {
        return false;
    }
})();

// Restore a Map from localStorage. Drops any entries past their TTL
// at rehydration time so stale data never lingers. Catches errors —
// localStorage can throw on quota / private mode / disabled cookies.
const _rehydrateCache = (storageKey, map, ttl) => {
    if (!_hasLocalStorage) return;
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return;
        const now = Date.now();
        for (const k of Object.keys(parsed)) {
            const entry = parsed[k];
            if (entry && entry.timestamp && (now - entry.timestamp) < ttl) {
                map.set(k, entry);
            }
        }
    } catch (err) {
        console.warn(`[GooglePlaces persist] rehydrate "${storageKey}" failed:`, err && err.message);
    }
};

// Persist a Map back to localStorage. Debounced via microtask so we
// don't pay JSON.stringify on every cache write within a single tick
// (which can happen with the parallel autocomplete + details flows).
const _persistTimers = {};
const _persistCache = (storageKey, map) => {
    if (!_hasLocalStorage) return;
    if (_persistTimers[storageKey]) return; // already scheduled
    _persistTimers[storageKey] = setTimeout(() => {
        _persistTimers[storageKey] = null;
        try {
            const obj = {};
            map.forEach((value, key) => { obj[key] = value; });
            window.localStorage.setItem(storageKey, JSON.stringify(obj));
        } catch (err) {
            // QuotaExceededError most common cause — silently drop the
            // persist (in-memory cache still works). Re-thrown errors
            // would break the whole search flow.
            console.warn(`[GooglePlaces persist] write "${storageKey}" failed:`, err && err.message);
        }
    }, 250);
};

_rehydrateCache(LS_AUTOCOMPLETE_KEY, autocompleteCache, AUTOCOMPLETE_CACHE_TTL);
_rehydrateCache(LS_PLACECOORD_KEY, placeCoordCache, PLACE_COORD_CACHE_TTL);

export const fetchPlacesAutocomplete = (searchKeyword, sessionToken) => {
    return new Promise((resolve,reject)=>{
        const { config } = firebase;
        const settings = store.getState()?.settingsdata?.settings;

        // Check autocomplete cache first
        const cacheKey = searchKeyword.toLowerCase().trim();
        const cached = autocompleteCache.get(cacheKey);
        if (cached && (Date.now() - cached.timestamp) < AUTOCOMPLETE_CACHE_TTL) {
            if (typeof __DEV__ !== 'undefined' && __DEV__) console.log(`[PLACES API CACHE] Cache hit for "${searchKeyword}"`);
            resolve(cached.results);
            return;
        }

        // Get caller information from stack trace
        const stack = new Error().stack;
        const callerMatch = stack.match(/at\s+(\S+)\s+\(/g);
        const caller = callerMatch && callerMatch[1] ? callerMatch[1].replace('at ', '').replace(' (', '') : 'unknown';

        console.debug(`[PLACES API] Autocomplete request: "${searchKeyword}"`);

        let host = window && window.location && settings.CompanyWebsite === window.location.origin? window.location.origin : `https://${config.projectId}.web.app`
        fetch(`${host}/googleapi`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey)
            },
            body: JSON.stringify({
                "searchKeyword": searchKeyword,
                "sessiontoken": sessionToken
            })
        }).then(response => {
            return response.json();
        })
        .then(json => {
            if(json && json.searchResults) {
                // Cache the result
                if (autocompleteCache.size >= AUTOCOMPLETE_CACHE_MAX) {
                    const oldestKey = autocompleteCache.keys().next().value;
                    autocompleteCache.delete(oldestKey);
                }
                autocompleteCache.set(cacheKey, { results: json.searchResults, timestamp: Date.now() });
                _persistCache(LS_AUTOCOMPLETE_KEY, autocompleteCache);

                // Log successful API call
                logGoogleAPIToFirebase(
                    'places_autocomplete',
                    { searchKeyword, sessionToken },
                    { resultCount: json.searchResults.length },
                    caller,
                    true
                ).catch(err => console.log('[GOOGLE API LOG] Failed to log:', err));

                resolve(json.searchResults);
            }else{
                // Log failed API call
                logGoogleAPIToFirebase(
                    'places_autocomplete',
                    { searchKeyword, sessionToken },
                    { error: json.error },
                    caller,
                    false
                ).catch(err => console.log('[GOOGLE API LOG] Failed to log:', err));

                reject(json.error);
            }
        }).catch(error=>{
            console.log(error);

            // Log error
            logGoogleAPIToFirebase(
                'places_autocomplete',
                { searchKeyword, sessionToken },
                { error: error.message || 'Call Error' },
                caller,
                false
            ).catch(err => console.log('[GOOGLE API LOG] Failed to log:', err));

            reject("fetchPlacesAutocomplete Call Error")
        })
    });
}

export const fetchCoordsfromPlace = (place_id) => {
    return new Promise((resolve,reject)=>{
        const { config } = firebase;
        const settings = store.getState()?.settingsdata?.settings;

        // Check place coord cache first
        const cached = placeCoordCache.get(place_id);
        if (cached && (Date.now() - cached.timestamp) < PLACE_COORD_CACHE_TTL) {
            console.log(`[PLACE DETAILS CACHE] Cache hit for place_id: ${place_id}`);
            resolve(cached.coords);
            return;
        }

        // Get caller information from stack trace
        const stack = new Error().stack;
        const callerMatch = stack.match(/at\s+(\S+)\s+\(/g);
        const caller = callerMatch && callerMatch[1] ? callerMatch[1].replace('at ', '').replace(' (', '') : 'unknown';

        console.log(`[PLACE DETAILS API] Fetching coords for place_id: ${place_id}`);

        let host = window && window.location && settings.CompanyWebsite === window.location.origin? window.location.origin : `https://${config.projectId}.web.app`
        fetch(`${host}/googleapi`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey)
            },
            body: JSON.stringify({
                "place_id": place_id
            })
        }).then(response => {
            return response.json();
        })
        .then(json => {
            if(json && json.coords) {
                // Cache the result
                if (placeCoordCache.size >= PLACE_COORD_CACHE_MAX) {
                    const oldestKey = placeCoordCache.keys().next().value;
                    placeCoordCache.delete(oldestKey);
                }
                placeCoordCache.set(place_id, { coords: json.coords, timestamp: Date.now() });
                _persistCache(LS_PLACECOORD_KEY, placeCoordCache);

                // Log successful API call
                logGoogleAPIToFirebase(
                    'place_details',
                    { place_id },
                    { coords: json.coords },
                    caller,
                    true
                ).catch(err => console.log('[GOOGLE API LOG] Failed to log:', err));

                resolve(json.coords);
            }else{
                // Log failed API call
                logGoogleAPIToFirebase(
                    'place_details',
                    { place_id },
                    { error: json.error },
                    caller,
                    false
                ).catch(err => console.log('[GOOGLE API LOG] Failed to log:', err));

                reject(json.error);
            }
        }).catch(error=>{
            console.log(error);

            // Log error
            logGoogleAPIToFirebase(
                'place_details',
                { place_id },
                { error: error.message || 'Call Error' },
                caller,
                false
            ).catch(err => console.log('[GOOGLE API LOG] Failed to log:', err));

            reject("fetchCoordsfromPlace Call Error")
        })
    });
}


// Track all Google API calls for monitoring
let geocodingCallCount = 0;
let lastGeocodeLogTime = Date.now();
let lastGeocodeCallTime = 0;
const GEOCODE_THROTTLE_MS = 1000; // Minimum 1 second between API calls
const geocodeCache = new Map(); // Simple in-memory cache
const CACHE_TTL = 300000; // 5 minutes cache TTL

// Helper function to remove undefined values from objects (Firebase doesn't allow undefined)
const sanitizeForFirebase = (obj) => {
    if (obj === null || obj === undefined) return null;
    if (typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(sanitizeForFirebase);
    
    const sanitized = {};
    for (const key in obj) {
        if (obj[key] !== undefined) {
            sanitized[key] = sanitizeForFirebase(obj[key]);
        }
    }
    return sanitized;
};

// Firebase logging for ALL Google API activity (security monitoring & cost tracking)
const logGoogleAPIToFirebase = async (apiType, requestData, responseData, caller, success = true) => {
    try {
        const { database } = firebase;
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
        const timestamp = Date.now();
        const auth = store.getState()?.auth;
        const userId = auth?.profile?.uid || 'anonymous';
        const userType = auth?.profile?.usertype || 'unknown';
        
        // Import ref and push from firebase/database for client-side
        const { ref, push, set, get } = await import('firebase/database');
        
        // Sanitize data to remove undefined values
        const sanitizedRequestData = sanitizeForFirebase(requestData);
        const sanitizedResponseData = sanitizeForFirebase(responseData);
        
        // Log to Firebase under google_api_logs/YYYY-MM-DD/
        const logRef = push(ref(database, `google_api_logs/${today}`));
        await set(logRef, {
            apiType: apiType, // 'geocoding', 'places_autocomplete', 'place_details', 'directions', 'distance_matrix'
            requestData: sanitizedRequestData,
            responseData: sanitizedResponseData,
            success: success,
            caller: caller,
            userId: userId,
            userType: userType,
            timestamp: timestamp,
            date: today
        });
        
        // Update daily counter by API type using get/set instead of transaction
        const counterRef = ref(database, `google_api_stats/${today}/${apiType}`);
        const counterSnapshot = await get(counterRef);
        const currentCount = counterSnapshot.exists() ? counterSnapshot.val() : 0;
        await set(counterRef, currentCount + 1);
        
        // Update total daily counter
        const totalRef = ref(database, `google_api_stats/${today}/total`);
        const totalSnapshot = await get(totalRef);
        const currentTotal = totalSnapshot.exists() ? totalSnapshot.val() : 0;
        await set(totalRef, currentTotal + 1);
    } catch (error) {
        console.log('[GOOGLE API LOG] Error logging to Firebase:', error);
    }
};

export const fetchAddressfromCoords = (latlng) => {
    return new Promise((resolve,reject)=>{
        const { config } = firebase;
        const settings = store.getState()?.settingsdata?.settings;
        const now = Date.now();
        
        // Check cache first
        const cacheKey = latlng;
        const cached = geocodeCache.get(cacheKey);
        if (cached && (now - cached.timestamp < CACHE_TTL)) {
            console.log(`[GEOCODING CACHE] Cache hit for ${latlng}`);
            resolve(cached.address);
            return;
        }
        
        // Throttle API calls
        const timeSinceLastCall = now - lastGeocodeCallTime;
        if (timeSinceLastCall < GEOCODE_THROTTLE_MS) {
            console.warn(`[GEOCODING THROTTLE] Blocked call - too soon (${timeSinceLastCall}ms since last call)`);
            // Return cached value if available, even if expired
            if (cached) {
                resolve(cached.address);
                return;
            }
            reject("Geocoding throttled - too many requests");
            return;
        }
        
        // Log geocoding API usage with stack trace
        geocodingCallCount++;
        lastGeocodeCallTime = now;
        const timeSinceLastLog = now - lastGeocodeLogTime;
        
        // Get caller information from stack trace
        const stack = new Error().stack;
        const callerMatch = stack.match(/at\s+(\S+)\s+\(/g);
        const caller = callerMatch && callerMatch[1] ? callerMatch[1].replace('at ', '').replace(' (', '') : 'unknown';
        
        // Always log for debugging the 18k calls issue
        console.warn(`[GEOCODING API] Call #${geocodingCallCount} from: ${caller} | Coords: ${latlng}`);
        
        // Log summary every 10 calls or every minute
        if (geocodingCallCount % 10 === 0 || timeSinceLastLog > 60000) {
            console.warn(`[GEOCODING MONITOR] Total API calls: ${geocodingCallCount}, Last 10 calls in ${(timeSinceLastLog/1000).toFixed(1)}s`);
            lastGeocodeLogTime = now;
        }
        let host = window && window.location && settings.CompanyWebsite === window.location.origin? window.location.origin : `https://${config.projectId}.web.app`
        fetch(`${host}/googleapi`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey)
            },
            body: JSON.stringify({
                "latlng": latlng
            })
        }).then(response => {
            return response.json();
        })
        .then(json => {
            if(json && json.address) {
                // Cache the result
                geocodeCache.set(cacheKey, {
                    address: json.address,
                    timestamp: Date.now()
                });
                // Clean old cache entries if cache gets too large
                if (geocodeCache.size > 100) {
                    const oldestKey = geocodeCache.keys().next().value;
                    geocodeCache.delete(oldestKey);
                }
                
                // Log to Firebase for security monitoring
                logGoogleAPIToFirebase(
                    'geocoding',
                    { coordinates: latlng },
                    { address: json.address },
                    caller,
                    true
                ).catch(err => console.log('[GOOGLE API LOG] Failed to log:', err));
                
                resolve(json.address);
            }else{
                // Log failed attempts too
                logGoogleAPIToFirebase(
                    'geocoding',
                    { coordinates: latlng },
                    { error: json.error },
                    caller,
                    false
                ).catch(err => console.log('[GOOGLE API LOG] Failed to log:', err));
                reject(json.error);
            }
        }).catch(error=>{
            console.log(error);
            reject("fetchAddressfromCoords Call Error")
        })
    });
}

export const getDistanceMatrix = (startLoc, destLoc) => {
    return new Promise((resolve,reject)=>{
        const { config } = firebase;
        const settings = store.getState()?.settingsdata?.settings;

        // Check distance matrix cache first
        const cacheKey = `${startLoc}_${destLoc}`;
        const cached = distanceMatrixCache.get(cacheKey);
        if (cached && (Date.now() - cached.timestamp) < DISTANCE_MATRIX_CACHE_TTL) {
            console.log(`[DISTANCE MATRIX CACHE] Cache hit for ${startLoc} -> ${destLoc}`);
            resolve(cached.result);
            return;
        }

        // Get caller information from stack trace
        const stack = new Error().stack;
        const callerMatch = stack.match(/at\s+(\S+)\s+\(/g);
        const caller = callerMatch && callerMatch[1] ? callerMatch[1].replace('at ', '').replace(' (', '') : 'unknown';

        console.log(`[DISTANCE MATRIX API] Request: ${startLoc} -> ${destLoc}`);

        let host = window && window.location && settings.CompanyWebsite === window.location.origin? window.location.origin : `https://${config.projectId}.web.app`
        fetch(`${host}/googleapi`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey)
            },
            body: JSON.stringify({
                "start": startLoc,
                "dest": destLoc,
                "calltype": "matrix",
            })
        }).then(response => {
            return response.json();
        })
        .then(json => {
            if(json.error){
                console.log(json.error);

                // Log failed API call
                logGoogleAPIToFirebase(
                    'distance_matrix',
                    { start: startLoc, dest: destLoc },
                    { error: json.error },
                    caller,
                    false
                ).catch(err => console.log('[GOOGLE API LOG] Failed to log:', err));

                reject(json.error);
            }else{
                // Cache the result
                if (distanceMatrixCache.size >= DISTANCE_MATRIX_CACHE_MAX) {
                    const oldestKey = distanceMatrixCache.keys().next().value;
                    distanceMatrixCache.delete(oldestKey);
                }
                distanceMatrixCache.set(cacheKey, { result: json, timestamp: Date.now() });

                // Log successful API call
                logGoogleAPIToFirebase(
                    'distance_matrix',
                    { start: startLoc, dest: destLoc },
                    { distance: json.distance_in_km, duration: json.time_in_secs },
                    caller,
                    true
                ).catch(err => console.log('[GOOGLE API LOG] Failed to log:', err));

                resolve(json);
            }
        }).catch(error=>{
            console.log(error);

            // Log error
            logGoogleAPIToFirebase(
                'distance_matrix',
                { start: startLoc, dest: destLoc },
                { error: error.message || 'Call Error' },
                caller,
                false
            ).catch(err => console.log('[GOOGLE API LOG] Failed to log:', err));

            reject("getDistanceMatrix Call Error")
        })
    });
}

export const getDirectionsApi = (startLoc, destLoc, waypoints) => {
    return new Promise((resolve,reject)=>{
        const { config } = firebase;
        const settings = store.getState()?.settingsdata?.settings;

        // Check directions cache first
        const cacheKey = `${startLoc}_${destLoc}_${waypoints || ''}`;
        const cached = directionsCache.get(cacheKey);
        if (cached && (Date.now() - cached.timestamp) < DIRECTIONS_CACHE_TTL) {
            console.log(`[DIRECTIONS CACHE] Cache hit for ${startLoc} -> ${destLoc}`);
            resolve(cached.result);
            return;
        }

        // Get caller information from stack trace
        const stack = new Error().stack;
        const callerMatch = stack.match(/at\s+(\S+)\s+\(/g);
        const caller = callerMatch && callerMatch[1] ? callerMatch[1].replace('at ', '').replace(' (', '') : 'unknown';

        console.log(`[DIRECTIONS API] Request: ${startLoc} -> ${destLoc}${waypoints ? ' (with waypoints)' : ''}`);

        let host = window && window.location && settings.CompanyWebsite === window.location.origin? window.location.origin : `https://${config.projectId}.web.app`
        const body = {
            "start": startLoc,
            "dest": destLoc,
            "calltype": "direction",
        };
        if(waypoints){
            body["waypoints"] = waypoints;
        }
        fetch(`${host}/googleapi`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                "Authorization": "Basic " + base64.encode(config.projectId + ":" + AccessKey)
            },
            body: JSON.stringify(body)
        }).then(response => {
            return response.json();
        })
        .then(json => {
            if (json.hasOwnProperty('distance_in_km')) {
                // Cache the result
                if (directionsCache.size >= DIRECTIONS_CACHE_MAX) {
                    const oldestKey = directionsCache.keys().next().value;
                    directionsCache.delete(oldestKey);
                }
                directionsCache.set(cacheKey, { result: json, timestamp: Date.now() });

                // Log successful API call
                logGoogleAPIToFirebase(
                    'directions',
                    { start: startLoc, dest: destLoc, hasWaypoints: !!waypoints },
                    { distance: json.distance_in_km, duration: json.time_in_secs, polyline: !!json.polylinePoints },
                    caller,
                    true
                ).catch(err => console.log('[GOOGLE API LOG] Failed to log:', err));

                resolve(json);
            }else{
                console.log(json.error);

                // Log failed API call
                logGoogleAPIToFirebase(
                    'directions',
                    { start: startLoc, dest: destLoc, hasWaypoints: !!waypoints },
                    { error: json.error },
                    caller,
                    false
                ).catch(err => console.log('[GOOGLE API LOG] Failed to log:', err));

                reject(json.error);
            }
        }).catch(error=>{
            console.log(error);

            // Log error
            logGoogleAPIToFirebase(
                'directions',
                { start: startLoc, dest: destLoc, hasWaypoints: !!waypoints },
                { error: error.message || 'Call Error' },
                caller,
                false
            ).catch(err => console.log('[GOOGLE API LOG] Failed to log:', err));

            reject("getDirectionsApi Call Error")
        })
    });
}

